import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import { FC, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { MediaAsset, colors } from '@hazadapt-git/public-core-base'
import { IoEye, IoDownload } from 'react-icons/io5'
import { LoadingButton } from '../atoms'
import { primaryIconSize, customBorderRadius } from '../../lib/styles/universal'

interface MediaAssetItemProps {
    asset: MediaAsset
    onView: (asset: MediaAsset) => void
    onDownload: (asset: MediaAsset) => Promise<void>
}

export const MediaAssetItem: FC<MediaAssetItemProps> = ({
    asset,
    onView,
    onDownload,
}) => {
    const { classes } = useStyles()
    const [isHovered, setIsHovered] = useState(false)

    return (
        <Box className={classes.container}>
            <Box
                className={classes.cardWrapper}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Card sx={{ borderRadius: 0, boxShadow: 'none' }}>
                    <img
                        src={asset.url}
                        alt={asset.display_name}
                        className={classes.image}
                    />
                </Card>
                {isHovered && (
                    <Box className={classes.hoverOverlay}>
                        <Box className={classes.buttonStack}>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.hoverButton}
                                onClick={() => onView(asset)}
                            >
                                <IoEye size={primaryIconSize} />
                            </Button>
                            <LoadingButton
                                variant="outlined"
                                size="small"
                                className={classes.hoverButton}
                                onClickAsync={() => onDownload(asset)}
                            >
                                <IoDownload size={primaryIconSize} />
                            </LoadingButton>
                        </Box>
                    </Box>
                )}
            </Box>
            <Typography
                fontWeight={600}
                align="center"
                className={classes.title}
            >
                {asset.display_name}
            </Typography>
        </Box>
    )
}

const useStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '0.5rem',
        height: '100%',
    },
    cardWrapper: {
        position: 'relative',
        borderRadius: customBorderRadius,
        overflow: 'hidden',
        backgroundColor: colors.grays.SMOOTH,
        border: `2px solid ${colors.primary.CERULEAN}`,
        padding: '0.5rem',
        minHeight: '12rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        width: '100%',
        maxWidth: '100%',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
        transition: 'transform 0.3s ease-in-out',
        maxHeight: '12rem',
    },
    hoverOverlay: {
        position: 'absolute',
        top: '0.5rem',
        left: '0.5rem',
        right: '0.5rem',
        bottom: '0.5rem',
        background: colors.grays.BLANC,
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.75,
        transition: 'opacity 0.2s ease-in-out',
    },
    buttonStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        alignItems: 'center',
    },
    hoverButton: {
        color: colors.primary.CERULEAN,
        borderColor: colors.primary.CERULEAN,
        backgroundColor: colors.grays.BLANC,
        borderRadius: customBorderRadius,
        minWidth: '4rem',
        height: '3rem',
        padding: '1rem',
        boxShadow: `0 2px 8px ${colors.grays.NOIR}`,
        opacity: 1,
        '&:hover': {
            borderColor: colors.primary.CERULEAN,
            backgroundColor: colors.grays.BLANC,
            boxShadow: `0 4px 12px ${colors.grays.NOIR}`,
            opacity: 1,
        },
    },
    title: {
        fontSize: '0.875rem',
        lineHeight: 1.4,
        textAlign: 'center',
        marginTop: '0.75rem',
        padding: '0 0.5rem',
        wordWrap: 'break-word',
        hyphens: 'auto',
        minHeight: '2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
})
